import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { adService } from "@/api/services"
import type {
  AdResponseDto,
  AdPromotionHistoryDto,
  AdRenewalHistoryDto,
} from "@/api/types/ad.types"
import Sidebar from "@/components/Sidebar"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Textarea } from "@/components/ui/textarea"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Loader2, ArrowLeft, Trash2, Star, Zap, Sparkles, History, RefreshCw } from "lucide-react"

export default function AdDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [ad, setAd] = useState<AdResponseDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [deleting, setDeleting] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteDescription, setDeleteDescription] = useState("")
  const [promotions, setPromotions] = useState<AdPromotionHistoryDto[]>([])
  const [renewals, setRenewals] = useState<AdRenewalHistoryDto[]>([])
  const [loadingPromotions, setLoadingPromotions] = useState(false)
  const [loadingRenewals, setLoadingRenewals] = useState(false)

  useEffect(() => {
    if (id) {
      loadAd()
    }
  }, [id])

  const loadAd = async () => {
    if (!id) return
    setLoading(true)
    setError("")
    try {
      const data = await adService.getAdById(id)
      setAd(data)
      // Load promotion and renewal history
      loadPromotions()
      loadRenewals()
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to load ad"
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const loadPromotions = async () => {
    if (!id) return
    setLoadingPromotions(true)
    try {
      const data = await adService.getAdPromotions(id)
      setPromotions(data)
    } catch (err: any) {
      // Silently fail - promotions are optional
      console.error("Failed to load promotions:", err)
    } finally {
      setLoadingPromotions(false)
    }
  }

  const loadRenewals = async () => {
    if (!id) return
    setLoadingRenewals(true)
    try {
      const data = await adService.getAdRenewals(id)
      setRenewals(data)
    } catch (err: any) {
      // Silently fail - renewals are optional
      console.error("Failed to load renewals:", err)
    } finally {
      setLoadingRenewals(false)
    }
  }

  const handleDelete = async () => {
    if (!id) return

    if (!deleteDescription.trim()) {
      setError("Please provide a reason for deletion")
      return
    }

    setDeleting(true)
    setError("")
    setDeleteDialogOpen(false)

    try {
      await adService.deleteAd(id, deleteDescription.trim())
      // Navigate back to ads list after successful delete
      navigate("/ads")
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to delete ad"
      setError(errorMessage)
      setDeleting(false)
    }
  }

  const formatPrice = (price: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EUR",
    }).format(parseFloat(price))
  }

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "N/A"
    return new Date(dateString).toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  if (loading) {
    return (
      <div className="flex">
        <Sidebar />
        <main className="flex-1 ml-64 p-8 bg-background min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </main>
      </div>
    )
  }

  if (!ad) {
    return (
      <div className="flex">
        <Sidebar />
        <main className="flex-1 ml-64 p-8 bg-background min-h-screen">
          <div className="max-w-2xl">
            <Card className="p-6">
              <div className="text-center">
                <p className="text-destructive mb-4">{error || "Ad not found"}</p>
                <Button onClick={() => navigate("/ads")} variant="outline">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Ads
                </Button>
              </div>
            </Card>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="flex">
      <Sidebar />
      <main className="flex-1 ml-64 p-8 bg-background min-h-screen">
        <div className="max-w-6xl">
          <div className="flex items-center gap-4 mb-8">
            <Button variant="ghost" size="sm" onClick={() => navigate("/ads")}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Ads
            </Button>
            <h1 className="text-3xl font-bold text-foreground">Ad Details</h1>
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 mb-6">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Images Gallery */}
          {ad.images && ad.images.length > 0 && (
            <Card className="p-6 mb-6">
              <h2 className="text-lg font-semibold text-foreground mb-4">Images</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {ad.images.map((image, index) => (
                  <div
                    key={image.id || index}
                    className="relative aspect-square rounded-lg overflow-hidden border border-border bg-muted"
                  >
                    <img
                      src={image.uploadedFile?.url || "https://via.placeholder.com/400?text=Image+Not+Found"}
                      alt={`Ad image ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        // Prevent infinite loop by checking if already set to placeholder
                        if (!target.src.includes("placeholder")) {
                          target.src = "https://via.placeholder.com/400?text=Image+Not+Found"
                        }
                      }}
                    />
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Ad Information */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Info */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="text-2xl font-bold text-foreground mb-2">{ad.title}</h2>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline">{ad.adType}</Badge>
                      <Badge
                        variant={
                          ad.status === "Published"
                            ? "default"
                            : ad.status === "Draft"
                            ? "secondary"
                            : "outline"
                        }
                      >
                        {ad.status}
                      </Badge>
                      {ad.isTop && (
                        <Badge variant="default">
                          <Star className="mr-1 h-3 w-3" />
                          Top
                        </Badge>
                      )}
                      {ad.isUrgent && (
                        <Badge variant="destructive">
                          <Zap className="mr-1 h-3 w-3" />
                          Urgent
                        </Badge>
                      )}
                      {ad.isHighlight && (
                        <Badge variant="default" className="bg-yellow-500">
                          <Sparkles className="mr-1 h-3 w-3" />
                          Highlight
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-primary">{formatPrice(ad.priceEur)}</p>
                    {ad.allowExchange && (
                      <p className="text-xs text-muted-foreground mt-1">Exchange allowed</p>
                    )}
                  </div>
                </div>

                <div className="prose prose-sm max-w-none dark:prose-invert">
                  <p className="text-foreground whitespace-pre-wrap">{ad.description}</p>
                </div>
              </Card>

              {/* Motorcycle Details */}
              {ad.adType === "Motorcycle" && ad.motorcycleDetails && (
                <Card className="p-6">
                  <h2 className="text-lg font-semibold text-foreground mb-4">
                    Motorcycle Details
                  </h2>
                  <div className="grid grid-cols-2 gap-4">
                    {ad.motorcycleDetails.brandId && (
                      <div>
                        <Label className="text-muted-foreground">Brand ID</Label>
                        <p className="text-foreground font-medium">{ad.motorcycleDetails.brandId}</p>
                      </div>
                    )}
                    {ad.motorcycleDetails.modelId && (
                      <div>
                        <Label className="text-muted-foreground">Model ID</Label>
                        <p className="text-foreground font-medium">{ad.motorcycleDetails.modelId}</p>
                      </div>
                    )}
                    {ad.motorcycleDetails.motoType && (
                      <div>
                        <Label className="text-muted-foreground">Type</Label>
                        <p className="text-foreground font-medium">{ad.motorcycleDetails.motoType}</p>
                      </div>
                    )}
                    {ad.motorcycleDetails.year && (
                      <div>
                        <Label className="text-muted-foreground">Year</Label>
                        <p className="text-foreground font-medium">{ad.motorcycleDetails.year}</p>
                      </div>
                    )}
                    {ad.motorcycleDetails.mileage && (
                      <div>
                        <Label className="text-muted-foreground">Mileage</Label>
                        <p className="text-foreground font-medium">
                          {ad.motorcycleDetails.mileage.toLocaleString()} km
                        </p>
                      </div>
                    )}
                    {ad.motorcycleDetails.engineSize && (
                      <div>
                        <Label className="text-muted-foreground">Engine Size</Label>
                        <p className="text-foreground font-medium">
                          {ad.motorcycleDetails.engineSize} cc
                        </p>
                      </div>
                    )}
                  </div>
                </Card>
              )}

              {/* Gear Details */}
              {ad.adType === "Gear" && ad.gearDetails && (
                <Card className="p-6">
                  <h2 className="text-lg font-semibold text-foreground mb-4">Gear Details</h2>
                  <div className="grid grid-cols-2 gap-4">
                    {ad.gearDetails.gearType && (
                      <div>
                        <Label className="text-muted-foreground">Gear Type</Label>
                        <p className="text-foreground font-medium">{ad.gearDetails.gearType}</p>
                      </div>
                    )}
                    {ad.gearDetails.size && (
                      <div>
                        <Label className="text-muted-foreground">Size</Label>
                        <p className="text-foreground font-medium">{ad.gearDetails.size}</p>
                      </div>
                    )}
                    {ad.gearDetails.brand && (
                      <div>
                        <Label className="text-muted-foreground">Brand</Label>
                        <p className="text-foreground font-medium">{ad.gearDetails.brand}</p>
                      </div>
                    )}
                  </div>
                </Card>
              )}
            </div>

            {/* Sidebar Info */}
            <div className="space-y-6">
              {/* Location */}
              <Card className="p-6">
                <h2 className="text-lg font-semibold text-foreground mb-4">Location</h2>
                <div className="space-y-2">
                  <div>
                    <Label className="text-muted-foreground">Address</Label>
                    <p className="text-foreground font-medium">{ad.locationAddress}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">City</Label>
                    <p className="text-foreground font-medium">{ad.locationCity}</p>
                  </div>
                </div>
              </Card>

              {/* Dates */}
              <Card className="p-6">
                <h2 className="text-lg font-semibold text-foreground mb-4">Dates</h2>
                <div className="space-y-2 text-sm">
                  <div>
                    <Label className="text-muted-foreground">Created</Label>
                    <p className="text-foreground font-medium">{formatDate(ad.createdAt)}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Updated</Label>
                    <p className="text-foreground font-medium">{formatDate(ad.updatedAt)}</p>
                  </div>
                  {ad.publishedAt && (
                    <div>
                      <Label className="text-muted-foreground">Published</Label>
                      <p className="text-foreground font-medium">{formatDate(ad.publishedAt)}</p>
                    </div>
                  )}
                  {ad.expiresAt && (
                    <div>
                      <Label className="text-muted-foreground">Expires</Label>
                      <p className="text-foreground font-medium">{formatDate(ad.expiresAt)}</p>
                    </div>
                  )}
                  {ad.highlightUntil && (
                    <div>
                      <Label className="text-muted-foreground">Highlight Until</Label>
                      <p className="text-foreground font-medium">{formatDate(ad.highlightUntil)}</p>
                    </div>
                  )}
                  {ad.topUntil && (
                    <div>
                      <Label className="text-muted-foreground">Top Until</Label>
                      <p className="text-foreground font-medium">{formatDate(ad.topUntil)}</p>
                    </div>
                  )}
                  {ad.urgentUntil && (
                    <div>
                      <Label className="text-muted-foreground">Urgent Until</Label>
                      <p className="text-foreground font-medium">{formatDate(ad.urgentUntil)}</p>
                    </div>
                  )}
                </div>
              </Card>

              {/* Promotion History */}
              <Card className="p-6">
                <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <History className="h-5 w-5" />
                  Promotion History
                </h2>
                {loadingPromotions ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : promotions.length > 0 ? (
                  <div className="space-y-3">
                    {promotions.map((promotion) => (
                      <div
                        key={promotion.id}
                        className="border rounded-lg p-3 bg-muted/50"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <Badge variant="outline">{promotion.promotionType}</Badge>
                        </div>
                        <div className="text-xs text-muted-foreground space-y-1">
                          <p>Purchased: {formatDate(promotion.purchasedAt)}</p>
                          <p>Expires: {formatDate(promotion.expiresAt)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No promotion history</p>
                )}
              </Card>

              {/* Renewal History */}
              <Card className="p-6">
                <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <RefreshCw className="h-5 w-5" />
                  Renewal History
                </h2>
                {loadingRenewals ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : renewals.length > 0 ? (
                  <div className="space-y-3">
                    {renewals.map((renewal) => (
                      <div
                        key={renewal.id}
                        className="border rounded-lg p-3 bg-muted/50"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">Renewal</span>
                          <span className="text-sm font-medium text-primary">
                            {formatPrice(renewal.priceEur)}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground space-y-1">
                          <p>Renewed: {formatDate(renewal.renewedAt)}</p>
                          <p>Expires: {formatDate(renewal.expiresAt)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No renewal history</p>
                )}
              </Card>

              {/* Actions */}
              <Card className="p-6">
                <h2 className="text-lg font-semibold text-foreground mb-4">Actions</h2>
                <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="destructive" className="w-full" disabled={deleting}>
                      {deleting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete Ad
                        </>
                      )}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Delete Ad?</DialogTitle>
                      <DialogDescription>
                        Are you sure you want to delete this ad? This action cannot be undone.
                        Please provide a reason for deletion.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="delete-description">
                          Reason for deletion <span className="text-destructive">*</span>
                        </Label>
                        <Textarea
                          id="delete-description"
                          placeholder="e.g., Ad violates marketplace rules (prohibited content)"
                          value={deleteDescription}
                          onChange={(e) => setDeleteDescription(e.target.value)}
                          rows={4}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setDeleteDialogOpen(false)
                          setDeleteDescription("")
                        }}
                        disabled={deleting}
                      >
                        Cancel
                      </Button>
                      <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
                        {deleting ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Deleting...
                          </>
                        ) : (
                          "Delete"
                        )}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}

